/**
 * Spacing for the quick access menu, so the row visibly runs off the screen.
 *
 * The menu holds more items than fit on a phone, and the only thing telling a
 * member so is that the next icon is sliced by the screen edge. Where that cut
 * lands cannot be left to chance.
 *
 * With a fixed 16px gap the five production icons sit at 24, 115, 206, 297 and
 * 388. A 390px iPhone therefore cuts the fifth icon 2px in — an invisible
 * sliver, and the edge fade sat right on top of it, so the menu looked like it
 * ended at icon four. A 430px phone got a clean half-icon from the same code.
 * The cue only worked on screen sizes nobody had checked.
 *
 * So the gap is computed from the measured width instead of being fixed. It is
 * the one measurement worth bending: anything from 8 to 34px reads as
 * deliberate spacing, and bending it lets us put the screen edge through the
 * middle of an icon on any screen.
 */

import { theme } from "../theme/theme";

/** Inset before the first icon. Fixed, so the row always starts in the same place. */
export const EDGE_PAD = theme.spacing.md + theme.spacing.sm;

/** Used as-is whenever the row already fits, and as the "natural" spacing to aim near. */
export const DEFAULT_GAP = theme.spacing.md;

/** Outside this range the spacing stops looking like a choice. */
export const MIN_GAP = 8;
export const MAX_GAP = 34;

/** How much of the sliced icon stays on screen. Just under half reads as cut, not broken. */
export const PEEK = 0.45;

/**
 * Gap that puts the right-hand screen edge through the middle of an icon.
 *
 * Returns `DEFAULT_GAP` when the row already fits — a menu with nothing hidden
 * must not pretend there is more, so nothing is bent and no cue is shown.
 *
 * @param {number} viewportWidth  measured width of the scrolling area
 * @param {number} itemWidth      measured width of one icon button
 * @param {number} itemCount      how many icons are in the row
 */
export function pickGap({ viewportWidth, itemWidth, itemCount }) {
  if (!viewportWidth || !itemWidth || itemCount < 2) return DEFAULT_GAP;

  const widthAtDefault =
    EDGE_PAD * 2 + itemCount * itemWidth + (itemCount - 1) * DEFAULT_GAP;
  if (widthAtDefault <= viewportWidth) return DEFAULT_GAP;

  // Room the whole icons share before the sliced one starts.
  const available = viewportWidth - EDGE_PAD - itemWidth * PEEK;

  // Try every possible number of whole icons and keep the spacing that puts the
  // edge nearest the middle of the next one. Solving for a single icon count is
  // not enough: on a narrow screen the gap that count needs can fall outside
  // the range above, and the clamp then drags the cut back towards the icon's
  // edge — recreating the invisible sliver this exists to prevent.
  let best = null;

  for (let whole = 1; whole <= itemCount - 1; whole += 1) {
    const gap = Math.round(
      Math.min(MAX_GAP, Math.max(MIN_GAP, available / whole - itemWidth)),
    );

    // Where the screen edge actually falls once that gap is applied.
    const sliceStart = EDGE_PAD + whole * (itemWidth + gap);
    const peek = (viewportWidth - sliceStart) / itemWidth;

    // Outside 0..1 the edge is not over this icon, so it tells us nothing.
    // Inside, an icon is genuinely cut and the row must therefore overflow.
    if (peek <= 0 || peek >= 1) continue;

    const miss = Math.abs(peek - PEEK);
    if (!best || miss < best.miss) best = { gap, miss };
  }

  return best ? best.gap : DEFAULT_GAP;
}

// ---------------------------------------------------------------------------
// The load hint: the row travels its whole width and comes back, so a member
// sees every item that is off screen rather than just the next one.
// ---------------------------------------------------------------------------

/**
 * Steady reading pace, in pixels per millisecond.
 *
 * The distance travelled depends on how many items are hidden, so the duration
 * has to follow it. A fixed duration would fly across a long menu and crawl
 * across a short one. (This is also why the hint is stepped frame by frame
 * instead of using `animated: true` — the browser's smooth scroll chooses its
 * own duration and cannot be told otherwise.)
 */
export const HINT_SPEED = 0.28;

/** Below this it reads as a twitch; above it, as a wait. */
export const HINT_MIN_MS = 420;
export const HINT_MAX_MS = 1100;

/** How long one leg of the hint should take to cover `distance` pixels. */
export function hintLegDuration(distance) {
  const travel = Math.max(0, distance || 0);
  return Math.max(HINT_MIN_MS, Math.min(HINT_MAX_MS, travel / HINT_SPEED));
}

/**
 * Fraction of an icon left visible at the screen edge for a given gap, or null
 * when the edge falls in a gap or the row fits. Used by the tests to check the
 * cut lands somewhere a person would notice.
 */
export function peekFraction({ viewportWidth, itemWidth, itemCount, gap }) {
  for (let whole = 0; whole < itemCount; whole += 1) {
    const start = EDGE_PAD + whole * (itemWidth + gap);
    if (viewportWidth > start && viewportWidth < start + itemWidth) {
      return (viewportWidth - start) / itemWidth;
    }
  }
  return null;
}

export default pickGap;
